"use client";

import { ptBR } from "date-fns/locale";
import { useUnit } from "effector-react";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { DateRange, SelectRangeEventHandler } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Formatter } from "@/lib/formatter";
import { cn } from "@/lib/utils";
import { updateDateRangeFilter } from "@/stores/DateRangeFilter/DateRangeFilterEvents";
import { DateRangeFilterStore } from "@/stores/DateRangeFilter/DateRangeFilterStore";

export function DatePickerWithRange({
  className,
}: React.HTMLAttributes<HTMLDivElement>) {
  const { startDate, endDate } = useUnit(DateRangeFilterStore);
  const isSameDate =
    startDate.getDate() === endDate.getDate() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getFullYear() === endDate.getFullYear();

  const [date, setDate] = React.useState<DateRange | undefined>({
    from: startDate,
    to: endDate,
  });

  React.useEffect(() => {
    setDate({
      from: startDate,
      to: endDate,
    });
  }, [startDate, endDate]);

  const handleDateClick = React.useCallback((selectedDate: DateRange) => {
    setDate(selectedDate);

    if (selectedDate.from) {
      const from = new Date(
        selectedDate.from.getFullYear(),
        selectedDate.from.getMonth(),
        selectedDate.from.getDate(),
        0,
        0,
        0
      );

      const to = selectedDate.to
        ? new Date(
            selectedDate.to.getFullYear(),
            selectedDate.to.getMonth(),
            selectedDate.to.getDate(),
            23,
            59,
            59
          )
        : new Date(
            selectedDate.from.getFullYear(),
            selectedDate.from.getMonth(),
            selectedDate.from.getDate(),
            23,
            59,
            59
          );

      updateDateRangeFilter({ startDate: from, endDate: to });
    }
  }, []) as SelectRangeEventHandler;

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-fit justify-start text-left font-normal text-muted-foreground gap-2",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon />
            {startDate ? (
              endDate && !isSameDate ? (
                <>
                  {Formatter.date(startDate, {
                    formatStr: "dd MMM yyyy",
                  })}{" "}
                  -{" "}
                  {Formatter.date(endDate, {
                    formatStr: "dd MMM yyyy",
                  })}
                </>
              ) : (
                Formatter.date(startDate, {
                  formatStr: "dd MMM yyyy",
                })
              )
            ) : (
              <span>Selecione uma data</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={startDate || new Date()}
            selected={date}
            onSelect={handleDateClick}
            toDate={new Date()}
            locale={ptBR}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
